import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { toast } from "sonner";
import { Loader2, MailCheck, RefreshCw, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, apiError, getToken } from "@/lib/api";
import { PC_MARK } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";

const RESEND_COOLDOWN_SECONDS = 60;
const COOLDOWN_KEY = "pc_verify_resend_at";

function maskEmail(email) {
  if (!email || !email.includes("@")) return email || "your email";
  const [local, domain] = email.split("@");
  const head = local.slice(0, 2);
  return `${head}${local.length > 2 ? "***" : "*"}@${domain}`;
}

function cooldownRemaining() {
  const at = Number(localStorage.getItem(COOLDOWN_KEY) || 0);
  if (!at) return 0;
  const elapsed = Math.floor((Date.now() - at) / 1000);
  return Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed);
}

export default function VerifyRequired() {
  const { user, logout, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState(cooldownRemaining());
  const pollRef = useRef(null);

  // Cooldown ticker
  useEffect(() => {
    const t = setInterval(() => setCooldown(cooldownRemaining()), 1000);
    return () => clearInterval(t);
  }, []);

  // Poll auth state so a link clicked in another tab flips this screen automatically.
  useEffect(() => {
    if (!getToken()) return;
    pollRef.current = setInterval(() => { refreshUser(); }, 5000);
    return () => clearInterval(pollRef.current);
  }, [refreshUser]);

  const resend = useCallback(async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: user?.email });
      localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
      setCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success("Verification email sent. Please check your inbox and spam folder.");
    } catch (e) {
      toast.error(apiError(e) || "Could not send right now. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  }, [cooldown, busy, user]);

  const checkNow = useCallback(async () => {
    setChecking(true);
    try {
      await refreshUser();
      const { data } = await api.get("/auth/verification-status");
      if (!data.email_verified) {
        toast.info("Not verified yet. Click the link in your email, then try again.");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setChecking(false);
    }
  }, [refreshUser]);

  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.email_verified !== false) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4">
      <div data-testid="verify-required-screen" className="max-w-md w-full text-center pc-reveal">
        <img src={PC_MARK} alt="PropertyConnect" className="h-10 w-10 object-contain mx-auto mb-4" />
        <div className="flex flex-col items-center gap-3">
          <MailCheck className="h-12 w-12 text-accent" />
          <h1 className="font-display text-2xl font-bold text-slate-900">Verify your email to continue</h1>
          <p className="text-slate-500">
            We sent a verification link to{" "}
            <span data-testid="verify-required-email" className="font-semibold text-slate-700">{maskEmail(user.email)}</span>.
            Please confirm your email address to activate your PropertyConnect account.
          </p>
          <p className="text-sm text-slate-400">
            Can't find it? Check your spam or promotions folder. The link is valid for 24 hours.
          </p>

          <div className="w-full mt-4 space-y-3">
            <Button
              data-testid="verify-required-resend"
              onClick={resend}
              disabled={busy || cooldown > 0}
              className="w-full h-11 bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : cooldown > 0 ? `Resend available in ${cooldown}s` : "Resend verification email"}
            </Button>

            <Button
              data-testid="verify-required-refresh"
              onClick={checkNow}
              disabled={checking}
              variant="outline"
              className="w-full h-11 rounded-xl"
            >
              {checking ? <Loader2 className="h-5 w-5 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-2" />I've verified — continue</>}
            </Button>

            <button
              data-testid="verify-required-logout"
              onClick={logout}
              className="w-full inline-flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium pt-1"
            >
              <LogOut className="h-4 w-4" /> Log out / use another account
            </button>
          </div>
        </div>
      </div>
      <SiteFooter variant="minimal" />
    </div>
  );
}
